import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ACCESS_MODELS, ChoiceCard, FieldError } from './shared';
import type { ProductAccessModel } from '@/types';

export interface AccessSettingsValue {
  access_model: ProductAccessModel;
  default_role: string;
  member_org_id: string;
  resource: string;
  include_permissions_in_token: boolean;
}

const NONE = '__none__';

/** Access model, default role, resource audience and token-permission switch (shared by New app and Settings). */
export function AccessSettingsFields({
  value,
  onChange,
  roleNames,
  orgs = [],
  errors = {},
  idPrefix = 'f',
}: {
  value: AccessSettingsValue;
  onChange: (next: AccessSettingsValue) => void;
  /** When provided, the default role is chosen from these; otherwise it is typed. */
  roleNames?: string[];
  /** Organizations that can be the member organization for org_members apps. */
  orgs?: { id: string; name: string }[];
  errors?: { resource?: string; default_role?: string; member_org_id?: string };
  idPrefix?: string;
}) {
  const set = (patch: Partial<AccessSettingsValue>) => onChange({ ...value, ...patch });
  return (
    <div className="space-y-5">
      <fieldset className="space-y-2">
        <legend className="mb-2 text-[13px] font-medium">
          {idPrefix === 'f' ? 'Who can sign in' : 'Access model'}
        </legend>
        <div className="grid gap-2 sm:grid-cols-3">
          {(Object.keys(ACCESS_MODELS) as ProductAccessModel[]).map((k) => (
            <ChoiceCard
              key={k}
              type="radio"
              name={`${idPrefix}-model`}
              value={k}
              checked={value.access_model === k}
              onChange={() => set({ access_model: k })}
              title={ACCESS_MODELS[k].label}
              description={ACCESS_MODELS[k].desc}
            />
          ))}
        </div>
      </fieldset>

      {value.access_model === 'org_members' && (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-org`}>Member organization</Label>
          <Select
            value={value.member_org_id || NONE}
            onValueChange={(v) => set({ member_org_id: v === NONE ? '' : v })}
          >
            <SelectTrigger id={`${idPrefix}-org`} aria-invalid={!!errors.member_org_id}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Choose an organization…</SelectItem>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError>{errors.member_org_id}</FieldError>
          <p className="text-xs text-muted-foreground">
            Members of this organization sign in automatically.
          </p>
        </div>
      )}

      {value.access_model === 'org_members' && (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-default`}>
            Default role for organization members
            {!roleNames && <span className="ml-1.5 text-xs font-normal text-muted-foreground">optional</span>}
          </Label>
          {roleNames ? (
            <Select
              value={value.default_role || NONE}
              onValueChange={(v) => set({ default_role: v === NONE ? '' : v })}
            >
              <SelectTrigger id={`${idPrefix}-default`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No default role</SelectItem>
                {roleNames.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id={`${idPrefix}-default`}
              className="font-mono"
              maxLength={40}
              placeholder="member"
              spellCheck={false}
              autoCapitalize="off"
              value={value.default_role}
              onChange={(e) => set({ default_role: e.target.value })}
              aria-invalid={!!errors.default_role}
            />
          )}
          <FieldError>{errors.default_role}</FieldError>
          <p className="text-xs text-muted-foreground">
            {roleNames
              ? roleNames.length
                ? 'Members of the organization get this role unless a grant says otherwise.'
                : 'Create roles on the Roles tab to pick a default.'
              : 'Create a role with this name afterwards so members get its permissions.'}
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-res`}>
          Resource audience
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">optional</span>
        </Label>
        <Input
          id={`${idPrefix}-res`}
          type="url"
          inputMode="url"
          className="font-mono"
          placeholder="https://api.example.com"
          value={value.resource}
          onChange={(e) => set({ resource: e.target.value })}
          aria-invalid={!!errors.resource}
        />
        <FieldError>{errors.resource}</FieldError>
        <p className="text-xs text-muted-foreground">
          RFC 8707 resource. When set, tokens for this app use it as{' '}
          <code className="text-[11.5px]">aud</code>.
        </p>
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3.5">
        <div>
          <b id={`${idPrefix}-perm-l`} className="text-[13.5px] font-semibold">
            Include permissions in access tokens
          </b>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            Roles are always emitted when the <code className="text-[11.5px]">products</code> scope
            is requested.
          </p>
        </div>
        <Switch
          aria-labelledby={`${idPrefix}-perm-l`}
          checked={value.include_permissions_in_token}
          onCheckedChange={(c) => set({ include_permissions_in_token: c })}
        />
      </div>
    </div>
  );
}
